<template>
    <div id="minimal-content" class="module-login">
        <h1>Anmeldung</h1>

        <div v-if="errorMessage" class="status-messages text-danger" v-html="errorMessage"></div>

        <form method="post" name="login" action="" id="login">
            <div class="formTable">
                <div id="intranet-user" class="content-row">
                    <form-input name="email" v-model="input.email" placeholder="E-Mail-Adresse" :class="{invalid: $store.state.form.errors.email}" v-focus/>
                    <form-error name="email"/>
                    <br/>
                </div>

                <div id="intranet-pass" class="content-row">
                    <form-input-password name="password" v-model="input.password" placeholder="Passwort" :invalid="!!$store.state.form.errors.password"/>
                    <form-error name="password"/>
                </div>

                <form-submit-row @submit="submit" :floating="false" class="content-row">
                    <form-input type="submit" name="submit" model-value="Anmelden" style="width: 100%;"/>
                </form-submit-row>

                <div class="text-center recover-password-text">
                    <router-link :to="{ name: 'recover-password' }">Passwort vergessen?</router-link>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
    import GlobalNewsListPopup from '@/views/global-news/components/GlobalNewsListPopup.vue';
    import axios from 'axios';
    import localforage from 'localforage';
    import FormError from '../components/form/FormError.vue';
    import FormInput from '../components/form/FormInput.vue';
    import FormInputPassword from '../components/form/FormInputPassword.vue';
    import FormSubmitRow from '../components/form/FormSubmitRow.vue';
    import {formSubmit} from '../library/form';
    import LocalStorage from '../library/localStorage';
    import {Popup} from '../library/popup';
    import {NoOperation} from '../library/utils';
    import {useRoute, useRouter} from 'vue-router';
    import {useStore} from 'vuex';
    import {ref, reactive, onMounted} from 'vue';

    const route        = useRoute();
    const store        = useStore();
    const router       = useRouter();
    const errorMessage = ref<Nullable<string>>(null);
    const input        = reactive({
        email: '',
        password: '',
    });

    onMounted(() =>
    {
        try
        {
            LocalStorage.clear();
            window.localStorage.clear();
            window.sessionStorage.clear();
        }
        catch (e)
        {
            console.error(e);
        }
        // clear non spa-store
        try
        {
            const nonSpaStore = localforage.createInstance({
                name: 'portunaStorageNonSPA',
                storeName: 'temporary',
            });
            nonSpaStore.clear().then(NoOperation);
        }
        catch (e)
        {
            console.error(e);
        }
    });

    const openLoginPopup = (allUnreadNews: IGlobalNewsPopupEntry[]) =>
    {
        let popupData;
        if (allUnreadNews.length > 1)
        {
            popupData = {
                title: 'Wir bringen Sie auf den neuesten Stand',
                persistent: true,
                component: GlobalNewsListPopup,
                componentProps: {
                    allUnreadNews,
                },
                buttons: [],
            };
        }
        else
        {
            const news = allUnreadNews[allUnreadNews.length - 1];
            popupData  = {
                title: news.title,
                text: news.content,
                persistent: true,
                popupClass: ['fr-view', 'message-content'],
                buttons: [
                    {
                        type: 'cancel',
                        title: 'Schließen und zukünftig verbergen',
                        icon: 'close',
                        cmd: (popup: Popup) =>
                        {
                            axios
                            .post('/application/application/mark-global-news-read', {
                                ids: [news.id],
                            })
                            .catch(NoOperation);
                            popup.close();
                        },
                    },
                    {
                        type: 'success',
                        title: 'Meldung anzeigen',
                        icon: 'remove_red_eye',
                        cmd: (popup: Popup) =>
                        {
                            router
                            .push({path: '/news/global/view/id/' + news.id})
                            .then(popup.close)
                            .catch(NoOperation);
                        },
                    },
                ],
            };
        }

        const popup = new Popup(popupData as PopupOptions);

        popup.open();
    };

    const submit = (): void =>
    {
        formSubmit(
            '/login',
            {messageType: 'none'},
            {
                email: input.email,
                password: input.password,
                referrer: route.params.referrer || null,
            },
        )
        .then((loginData) =>
        {
            store.commit('setLayoutDataLoaded', false);
            router.push({path: loginData.referrer}).finally(() =>
            {
                if (loginData?.no_news_popup)
                {
                    return;
                }

                // wait for navigation (layout-swap) done, so popup-portal-target is existing
                axios
                .get('/application/application/fetch-login-popup-data')
                .then(({data: {data}}) =>
                {
                    if (data.all_unread_news && data.all_unread_news.length)
                    {
                        openLoginPopup(data.all_unread_news)
                    }
                });
            });
        })
        .catch((error) =>
        {
            try
            {
                errorMessage.value = error.data.message;
            }
            catch (e)
            {
                console.error('[Login]', error, e);
                errorMessage.value = 'Es ist ein Fehler aufgetreten, bitte versuchen Sie es später erneut.';
            }
        });
    };

</script>